/**
 * Looks: alternative chrome for a control, chosen by name.
 *
 * A theme draws every button the same way, which is what a theme is for. A scene wants a few
 * buttons that are not like the others - the glowing "Enter" at the bottom of a character select,
 * the calling plates down its side, a class medallion. Those were each painted by hand, with
 * hit testing done by hand beside them; a look is that painting handed to a real `Button`, so
 * focus, keys and clicks come free.
 *
 * Looks are a registry of names, like flares and glyphs, so a look can come from data and an
 * application can register its own or replace a built-in. A look paints the chrome only; the
 * control still draws its own caption, in the color and font the look asks for.
 */
import type { Color } from "../../common/color.js";
import type { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import type { Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { ProgressVisual, Theme, VisualState } from "../themes/Theme.js";
import { resolveColorRef, type ColorRef } from "./ArtControl.js";

export interface LookContext {
  rc: RenderContext;
  theme: Theme;
  rect: Rect;
  state: VisualState;
  /** The chosen one of a set: a selected plate, the current medallion. */
  lit: boolean;
  /** The look's color: the control's `lookColor`, or the theme's accent. */
  color: Color;
  /** The desktop's clock, in seconds. */
  time: number;
  /** The control being dressed, for a look that keeps state between frames. */
  control?: Control;
}

export interface Look {
  /** Paint a button's chrome. The button draws its caption over it. */
  button?(ctx: LookContext): void;
  /** The caption's color on this look; the theme's text color when absent. */
  buttonText?(ctx: LookContext): Color;
  /** The caption's font; the button's own when absent. */
  buttonFont?(ctx: LookContext, font: FontSpec): FontSpec;
  /** True when the caption belongs in the middle whatever the button's `textAlign` says. */
  centerText?: boolean;
  /** Paint a panel's background. Its children are drawn over it. */
  panel?(ctx: LookContext): void;
  /** Paint a whole progress bar, fill and all. */
  progress?(ctx: LookContext, p: ProgressVisual): void;
  /** True while a control wearing it needs a frame every tick. */
  animates?(ctx: Pick<LookContext, "state" | "lit">, kind: LookKind): boolean;
  /**
   * The silhouette the look paints inside `rect`, as a rounded rect: what a glow round a control
   * wearing it follows. A medallion is a circle inside its square; a plate has its own corners.
   * The rect itself with the theme's corner radius when absent.
   */
  shape?(rect: Rect): LookShape;
}

export interface LookShape {
  rect: Rect;
  radius: number;
}

/** The shape a control's look paints, or null when it wears none or the look does not say. */
export function lookShape(control: unknown, rect: Rect): LookShape | null {
  const name = (control as { look?: unknown } | null)?.look;
  return typeof name === "string" ? (getLook(name)?.shape?.(rect) ?? null) : null;
}

export type LookKind = "button" | "panel" | "progress";

const registry = new Map<string, Look>();

/** Register a look. Re-registering a name replaces it, which is how an application restyles one. */
export function registerLook(name: string, look: Look): void {
  registry.set(name, look);
}

export function getLook(name: string | null | undefined): Look | null {
  return name ? (registry.get(name) ?? null) : null;
}

/** The registered names that can dress a control of this kind. */
export function lookNames(kind?: LookKind): string[] {
  return [...registry.entries()]
    .filter(([, look]) => kind === undefined || typeof look[kind] === "function")
    .map(([name]) => name)
    .sort();
}

/** Build the context a look paints with. */
export function lookContext(
  control: Control,
  rc: RenderContext,
  theme: Theme,
  lookColor: ColorRef | null,
  lit: boolean,
): LookContext {
  return {
    rc,
    theme,
    rect: control.localRect,
    state: control.visualState(),
    lit,
    color: resolveColorRef(lookColor, theme.palette) ?? theme.palette.accent,
    time: control.desktop?.frameTime ?? 0,
    control,
  };
}

/**
 * Keeps a control registered as a desktop animator for exactly as long as its look is moving.
 * One per control; `sync` is called from the control's paint.
 */
export class LookAnimator {
  #owner: Control;
  #with: Desktop | null = null;

  constructor(owner: Control) {
    this.#owner = owner;
  }

  sync(want: boolean): void {
    const desktop = this.#owner.desktop;
    const target = want && desktop && !desktop.reducedMotion ? desktop : null;
    if (target === this.#with) return;
    this.#with?.removeAnimator(this.#owner);
    this.#with = target;
    target?.addAnimator(this.#owner);
  }
}

// The built-in looks - plate, card, medallion, ember, flare - are art markup: `library/looks.xml`,
// registered by `library/index.ts` when the art library loads.
